import React, { createContext, ReactNode, use, useEffect } from 'react'
import { usePublicStyling } from '@chilipiper/api-queries/src/workspace'
import { PersonalDetails } from '@chilipiper/api-client/src/workspace'
import { Context } from '../components/types'
import { ParsedBlock, Block } from '../chat-data/types'
import { useNotificationSound } from '../hooks/useNotificationSound'
import { useTenantDetails } from '../api-queries/workspace'
import { useRoomDataContext } from '../chat-data/RoomDataContext'
import { useRoomAssignee } from '../xmpp-client/getNewRoomAssignees'
import { usePersonalDetails, useProfilePictureUrl } from '../api-queries/personal-details'
import { useMessageFeed } from './useMessageFeed'

export const MessageFeedContext = createContext<Context>({
  sendingMessage: false,
  sendMessage: () => undefined,
  prepareChat: () => Promise.resolve(),
  disableInteractions: false,
  workspaceId: '',
  pickReplyButton: () => undefined,
  submitForm: () => undefined,
  triggerMeetingFallback: () => undefined,
  isTyping: false,
  isLoading: false,
  reconnectingStatus: 'CONNECTED',
  blocks: [],
  selectMeetingSlot: () => undefined,
  tenantId: '',
  isNarrowScreen: false,
  piperAiActive: false,
  sendMessageToPiperAi: () => undefined,
  isWaitingForPiperAiResponse: false,
})

export interface Props {
  children?: ReactNode
  disableInteractions?: boolean
  isPreview?: boolean
  onBootstrapComplete?: (roomId: string, playbookId: string) => void
  tenantId: string
  userId?: string
  workspaceId?: string
  isNarrowScreen?: boolean
}

export const PreviewMessageFeedProvider = ({
  children,
  messages,
  logo,
  name,
  liveChatActive = false,
  disableInteractions = true,
  tenantId,
  workspaceId,
}: Props & {
  liveChatActive?: boolean
  logo?: string
  messages?: Block[]
  name?: string
}) => {
  const orgDetails = {
    logo,
    about: 'about',
    name,
  }

  const channel = orgDetails
    ? {
        id: 'bot',
        fullName: orgDetails.name ?? '',
        status: 'online',
        avatars: [orgDetails.logo ?? ''],
      }
    : undefined

  const value = {
    channel,
    sendingMessage: false,
    pickReplyButton: () => undefined,
    submitForm: () => undefined,
    blocks: messages ?? [],
    liveChatActive,
    isTyping: false,
    isPreview: true,
    disableInteractions,
    isLoading: false,
    workspaceId,
    selectMeetingSlot: () => undefined,
    triggerMeetingFallback: () => undefined,
    reconnectingStatus: 'CONNECTED' as const,
    sendMessage: () => undefined,
    prepareChat: () => Promise.resolve(),
    tenantId,
    roomId: 'roomId',
    isFormSubmitted: false,
    isNarrowScreen: false,
    piperAiActive: false,
    sendMessageToPiperAi: () => undefined,
    isWaitingForPiperAiResponse: false,
  }

  return <MessageFeedContext value={value}>{children}</MessageFeedContext>
}

export const MessageFeedProvider = ({
  children,
  disableInteractions = false,
  workspaceId,
  tenantId,
  onBootstrapComplete,
  isNarrowScreen = false,
}: Props) => {
  const { data: tenantDetails } = useTenantDetails(tenantId ?? '')
  const { data } = usePublicStyling(tenantId, workspaceId)
  const orgDetails = {
    logo: data?.logo,
    name: tenantDetails?.payload.companyName,
  }

  const {
    selectMeetingSlot,
    guestId,
    liveChatActive,
    sendingMessage,
    pickReplyButton,
    submitForm,
    blocks,
    isThrottling,
    sendLiveMessage,
    status,
    prepareChat,
    isLoading,
    roomId,
    triggerMeetingFallback,
    piperAiActive,
    sendMessageToPiperAi,
    isWaitingForPiperAiResponse,
  } = useMessageFeed({
    workspaceId,
    tenantId,
    onBootstrapComplete,
  })

  const assigneeId = useRoomAssignee({
    roomId: roomId ?? '',
    tenantId,
    workspaceId: workspaceId ?? '',
  })
  const { data: assignee } = usePersonalDetails(tenantId, assigneeId)
  const { data: assigneePicture } = useProfilePictureUrl(tenantId, assigneeId)

  const channel = orgDetails
    ? {
        id: 'bot',
        fullName: getName(assignee) ?? orgDetails.name ?? '',
        status: 'online',
        avatars: [assigneePicture?.url ?? orgDetails.logo ?? ''],
      }
    : undefined

  const { isInitial } = useRoomDataContext()
  useNotification(`${guestId}_guest`, blocks, isInitial)

  const value = {
    guestId,
    channel,
    liveChatActive,
    reconnectingStatus: status,
    sendingMessage,
    pickReplyButton,
    submitForm,
    tenantId,
    blocks,
    isTyping: isThrottling || isWaitingForPiperAiResponse,
    isPreview: false,
    disableInteractions,
    prepareChat,
    sendMessage: piperAiActive ? sendMessageToPiperAi : sendLiveMessage,
    isLoading,
    selectMeetingSlot,
    isNarrowScreen,
    triggerMeetingFallback,
    workspaceId,
    roomId,
    orgDetails,
    piperAiActive,
    sendMessageToPiperAi,
    isWaitingForPiperAiResponse,
  }

  return <MessageFeedContext value={value}>{children}</MessageFeedContext>
}

export const useMessageFeedContext = () => {
  return use(MessageFeedContext)
}

const useNotification = (
  guestId: string | undefined,
  events: Array<ParsedBlock>,
  isInitial: boolean
) => {
  const { play, isThrottled } = useNotificationSound()
  useEffect(() => {
    if (
      !isInitial &&
      events.some(event => {
        const isNewEvent = !isThrottled(event.timestamp)
        const isOwnEvent = event.author === guestId && event.type === 'LiveMessage'
        const isEventWorthNotifying = [
          'LiveMessage',
          'Message',
          'ReplyButtons',
          'FillForm',
          'BookMeeting',
        ].includes(event.type)
        return isEventWorthNotifying && !isOwnEvent && isNewEvent
      })
    ) {
      play()
    }
  }, [events, guestId, isThrottled, play, isInitial])
}

const getName = (assignee: PersonalDetails | undefined) => {
  if (assignee) {
    return `${assignee.firstName ?? ''} ${assignee.lastName ?? ''}`.trim() || assignee.email
  }
}
